import { useState, useEffect } from 'react';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { PluginMetricCards } from '@/app/components/PluginMetricCards';
import { PluginPageHeader } from '@/app/components/PluginPageHeader';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { useApiToken } from '@/auth/hooks';
import { BarChart3, SearchCheck, ShieldAlert } from 'lucide-react';

interface RolloutMetricDto {
  label: string;
  numerator: number;
  denominator: number;
  percentage: number;
}

interface ReindexProgressDto {
  currentDocumentCount: number;
  chunkReadyDocumentCount: number;
  completedDocumentCount: number;
  pendingDocumentCount: number;
  failedDocumentCount: number;
  embeddingReindexStatus: string;
  embeddingReindexCompletedAt: string | null;
  coveragePercentage: number;
}

interface RetrievalLatencyDto {
  sampleCount: number;
  averageMs: number | null;
  medianMs: number | null;
  p95Ms: number | null;
}

interface RolloutActivityDto {
  assistantResponseCount: number;
  tracedAssistantResponseCount: number;
  savedNoteCount: number;
  citedNoteCount: number;
  activeDayCount: number;
}

interface RolloutKpiDayDto {
  date: string;
  assistantResponses: number;
  hits: number;
  errors: number;
  averageLatencyMs: number | null;
}

interface RolloutKpiDto {
  id: string;
  slugId: string;
  windowDays: number;
  hitRate: RolloutMetricDto;
  recallProxy: RolloutMetricDto;
  errorRate: RolloutMetricDto;
  reindexProgress: ReindexProgressDto;
  retrievalLatency: RetrievalLatencyDto;
  activity: RolloutActivityDto;
  daily: RolloutKpiDayDto[];
  notes: string[];
}

function formatMs(value: number | null): string {
  return value == null ? '—' : `${Math.round(value)} ms`;
}

function formatPercent(value: number): string {
  return `${value.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 1 })} %`;
}

function formatShortDate(value: string): string {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

export function RolloutKpiPage() {
  const { activeWorkspace } = useWorkspaceStore();
  const { getAccessToken } = useApiToken();
  const [rolloutKpis, setRolloutKpis] = useState<RolloutKpiDto | null>(null);
  const [rolloutLoading, setRolloutLoading] = useState(false);
  const [rolloutError, setRolloutError] = useState<string | null>(null);

  const activeWorkspaceId = activeWorkspace?.id ?? null;

  useEffect(() => {
    if (!activeWorkspaceId) {
      setRolloutKpis(null);
      setRolloutError(null);
      setRolloutLoading(false);
      return;
    }

    let cancelled = false;

    async function loadRolloutKpis() {
      setRolloutLoading(true);
      setRolloutError(null);

      try {
        const token = await getAccessToken();
        const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await fetch(`/api/dotnet/workspaces/${activeWorkspaceId}/pilot-rollout-kpis`, { headers });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const payload = (await response.json()) as RolloutKpiDto;
        if (!cancelled) {
          setRolloutKpis(payload);
        }
      } catch {
        if (!cancelled) {
          setRolloutKpis(null);
          setRolloutError('Rollout-KPIs konnten nicht geladen werden.');
        }
      } finally {
        if (!cancelled) {
          setRolloutLoading(false);
        }
      }
    }

    void loadRolloutKpis();

    return () => {
      cancelled = true;
    };
  }, [activeWorkspaceId, getAccessToken]);

  return (
    <div className="flex h-full w-full min-w-0 flex-col gap-6 px-4 py-6 sm:px-6 xl:px-8 2xl:px-10">
      <PluginPageHeader group="admin" title="Sopho Quality" icon={BarChart3} />

      {!activeWorkspace ? (
        <div className="text-sm text-muted-foreground">
            Kein Workspace aktiv. Bitte erst einen Workspace anlegen oder laden.
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight text-foreground">Rollout-Metriken</h2>
              <p className="text-sm text-muted-foreground">Qualität, Reindex-Fortschritt und Retrieval-Signale des aktiven Workspaces.</p>
            </div>
            {rolloutKpis ? (
              <Badge variant="outline">
                {rolloutKpis.windowDays} Tage
              </Badge>
            ) : null}
          </div>

          {rolloutLoading ? (
            <div className="text-sm text-muted-foreground" data-testid="kpi-loading">Rollout-KPIs werden geladen…</div>
          ) : rolloutError ? (
            <div className="text-sm text-destructive" data-testid="kpi-error">{rolloutError}</div>
          ) : rolloutKpis ? (
            <>
              <PluginMetricCards
                className="gap-3 md:grid-cols-3"
                items={[
                  { id: 'hit-rate', label: rolloutKpis.hitRate.label, value: formatPercent(rolloutKpis.hitRate.percentage), detail: `${rolloutKpis.hitRate.numerator} / ${rolloutKpis.hitRate.denominator}`, icon: SearchCheck, tone: 'success' },
                  { id: 'recall', label: rolloutKpis.recallProxy.label, value: formatPercent(rolloutKpis.recallProxy.percentage), detail: `${rolloutKpis.recallProxy.numerator} / ${rolloutKpis.recallProxy.denominator}`, icon: BarChart3, tone: 'info' },
                  { id: 'error-rate', label: rolloutKpis.errorRate.label, value: formatPercent(rolloutKpis.errorRate.percentage), detail: `${rolloutKpis.errorRate.numerator} / ${rolloutKpis.errorRate.denominator}`, icon: ShieldAlert, tone: 'danger' },
                ]}
              />

              <div className="grid gap-3 lg:grid-cols-[1.4fr_1fr]">
                <div className="rounded-2xl border border-border/70 bg-card/95 p-4 shadow-sm space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold">Reindex-Fortschritt</div>
                      <div className="text-xs text-muted-foreground">
                        {rolloutKpis.reindexProgress.chunkReadyDocumentCount} von {rolloutKpis.reindexProgress.currentDocumentCount} Dokumenten mit SQL-Chunkabdeckung
                      </div>
                    </div>
                    {/* DevPlan 0125: EmbeddingReindexStatus-Badge entfernt — Workflow ist Dead Code, Status ist immer 'Removed'. */}
                  </div>
                  <Progress value={rolloutKpis.reindexProgress.coveragePercentage} />
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4 text-sm">
                    <div>
                      <div className="text-xs text-muted-foreground">Abdeckung</div>
                      <div className="font-semibold">{formatPercent(rolloutKpis.reindexProgress.coveragePercentage)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Completed</div>
                      <div className="font-semibold">{rolloutKpis.reindexProgress.completedDocumentCount}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Pending</div>
                      <div className="font-semibold">{rolloutKpis.reindexProgress.pendingDocumentCount}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Fehler</div>
                      <div className="font-semibold">{rolloutKpis.reindexProgress.failedDocumentCount}</div>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-border/70 bg-card/95 p-4 shadow-sm space-y-3">
                  <div>
                    <div className="text-sm font-semibold">Retrieval-Latenz</div>
                    <div className="text-xs text-muted-foreground">
                      {rolloutKpis.retrievalLatency.sampleCount} persistierte Trace-Samples
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <div className="text-xs text-muted-foreground">Mittel</div>
                      <div className="font-semibold">{formatMs(rolloutKpis.retrievalLatency.averageMs)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Median</div>
                      <div className="font-semibold">{formatMs(rolloutKpis.retrievalLatency.medianMs)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">P95</div>
                      <div className="font-semibold">{formatMs(rolloutKpis.retrievalLatency.p95Ms)}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-xs text-muted-foreground">Antworten</div>
                      <div className="font-semibold">{rolloutKpis.activity.assistantResponseCount}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Notizen</div>
                      <div className="font-semibold">{rolloutKpis.activity.savedNoteCount}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Trace-Samples</div>
                      <div className="font-semibold">{rolloutKpis.activity.tracedAssistantResponseCount}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Aktive Tage</div>
                      <div className="font-semibold">{rolloutKpis.activity.activeDayCount}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-border/70 bg-card/95 p-4 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold">Tägliche Stichprobe</div>
                  <div className="text-xs text-muted-foreground">letzte 7 Tage mit Aktivität</div>
                </div>
                <div className="mt-3 space-y-2">
                  {rolloutKpis.daily
                    .filter((day) => day.assistantResponses > 0 || day.errors > 0)
                    .slice(-7)
                    .map((day) => (
                      <div key={day.date} className="grid grid-cols-[72px_1fr_1fr_1fr] gap-3 text-sm">
                        <div className="text-muted-foreground">{formatShortDate(day.date)}</div>
                        <div>
                          <span className="font-semibold">{day.assistantResponses}</span> Antworten
                        </div>
                        <div>
                          <span className="font-semibold">{day.hits}</span> mit Quellen
                        </div>
                        <div>
                          <span className="font-semibold">{formatMs(day.averageLatencyMs)}</span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {rolloutKpis.notes.length > 0 ? (
                <div className="rounded-2xl border border-border/70 bg-card/95 p-4 shadow-sm">
                  <div className="text-sm font-semibold">Hinweise</div>
                  <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                    {rolloutKpis.notes.map((note) => (
                      <li key={note}>{note}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </>
          ) : (
            <div className="text-sm text-muted-foreground">Noch keine Rollout-KPIs verfügbar.</div>
          )}
        </div>
      )}
    </div>
  );
}
